import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Shield, Clock, Star, ArrowRight, Car } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BookingForm } from "@/components/BookingForm";
import { PHONE, TEL_URL, WHATSAPP_URL } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1534263873534-4d0d76aae42b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzY2VuaWMlMjBmb3Jlc3QlMjByb2FkJTIwZHJpdmluZyUyMGluZGlhfGVufDB8fHx8MTc4MDk5MTU0MHww&ixlib=rb-4.1.0&q=85";
const FLEET_IMG =
  "https://images.pexels.com/photos/17612417/pexels-photo-17612417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const destinations = [
  {
    name: "Ghatagaon Tarini Temple",
    img: "https://upload.wikimedia.org/wikipedia/commons/6/6b/Tarni_temple_Gate_view.JPG",
    tag: "Pilgrimage",
    blurb: "Maa Tarini darshan with comfortable round-trip arrangement.",
  },
  {
    name: "Khandadhar Waterfalls",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Khandadhar.jpg/1280px-Khandadhar.jpg",
    tag: "Day Trip",
    blurb: "244-metre tiered waterfall — one of Odisha's tallest.",
  },
  {
    name: "Sanaghagara & Beyond",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Sanaghagara_waterfall.jpg/1280px-Sanaghagara_waterfall.jpg",
    tag: "Local",
    blurb: "Sanaghagara, Murga Mahadev, Gonasika — full Keonjhar circuit.",
  },
];

const services = [
  { title: "Local & City Rides", desc: "Harichandanpur, Ghatagaon, Anandapur and nearby." },
  { title: "Outstation Trips", desc: "Bhubaneswar, Cuttack, Puri, Jamshedpur, Rourkela." },
  { title: "Airport Transfers", desc: "On-time pickups & drops to BBI / Jharsuguda." },
  { title: "Pilgrim Tours", desc: "Tarini Temple, Lingaraj, Jagannath Dham circuits." },
];

const Home = () => {
  return (
    <div className="App bg-[#0A0E17] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative h-[100vh] min-h-[640px] w-full overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Scenic Odisha road"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-20 md:pb-32">
          <p className="fade-up text-[11px] md:text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-6">
            · Harichandanpur · Ghatagaon · Keonjhar ·
          </p>
          <h1 className="fade-up delay-1 font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight text-white max-w-5xl">
            Amyee Travels.
            <br />
            <span className="italic text-[#D95D39]">Rides rooted</span> in Odisha.
          </h1>
          <p className="fade-up delay-2 mt-8 text-base md:text-lg text-gray-300 max-w-2xl leading-relaxed">
            Taxi services across Harichandanpur, Ghatagaon and the entire Keonjhar district —
            local trips, pilgrim tours and outstation drives, available around the clock.
          </p>

          <div className="fade-up delay-3 mt-10 flex flex-wrap gap-4">
            <a
              href={TEL_URL}
              data-testid="hero-call-button"
              className="bg-[#D95D39] hover:bg-[#E27D60] text-white px-8 py-4 flex items-center gap-3 transition-colors group"
            >
              <Phone className="w-4 h-4" />
              <span className="tracking-wider">Call {PHONE}</span>
              <ArrowRight className="w-4 h-4 -ml-1 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="border border-[#2C5F2D] bg-[#2C5F2D]/30 hover:bg-[#2C5F2D] text-white px-8 py-4 flex items-center gap-3 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="tracking-wider">WhatsApp {PHONE}</span>
            </a>
            <a
              href="#booking"
              data-testid="hero-book-button"
              className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E17] px-8 py-4 flex items-center gap-3 transition-colors"
            >
              Book Online
            </a>
          </div>

          <div
            data-testid="hero-price-tag"
            className="fade-up delay-3 mt-8 flex flex-wrap items-baseline gap-3"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Starting at</span>
            <span className="font-serif text-3xl md:text-4xl text-[#D4AF37]">
              ₹2,799<span className="text-gray-500 text-xl">/day*</span>
            </span>
            <span className="text-xs text-gray-500 italic">*Terms &amp; conditions apply</span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0A0E17] border-y border-white/10 py-6 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 text-2xl md:text-3xl font-serif italic text-gray-500">
              <span>Ghatagaon Tarini</span><span className="text-[#D4AF37]">◆</span>
              <span>Khandadhar Falls</span><span className="text-[#D4AF37]">◆</span>
              <span>Sanaghagara</span><span className="text-[#D4AF37]">◆</span>
              <span>Murga Mahadev</span><span className="text-[#D4AF37]">◆</span>
              <span>Gonasika</span><span className="text-[#D4AF37]">◆</span>
              <span>Anandapur</span><span className="text-[#D4AF37]">◆</span>
              <span>Bhubaneswar</span><span className="text-[#D4AF37]">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT + SERVICES */}
      <section className="bg-[#0A0E17] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">About us</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-white leading-tight tracking-tight">
                A local family of drivers,
                <span className="italic text-[#D95D39]"> driving you anywhere.</span>
              </h2>
              <p className="mt-8 text-gray-400 leading-relaxed max-w-md">
                Based in Harichandanpur, Amyee Travels has been moving pilgrims, families and
                businesses across Keonjhar for years. From a quick ride to Ghatagaon for darshan,
                to a multi-day Odisha tour — we keep the trip simple, comfortable and on time.
              </p>
              <div className="mt-10 flex flex-wrap gap-6 items-center">
                <a href={TEL_URL} className="flex items-center gap-3 text-white hover:text-[#D4AF37] transition-colors">
                  <Phone className="w-5 h-5 text-[#D95D39]" />
                  <span className="font-serif text-2xl">{PHONE}</span>
                </a>
              </div>
            </div>

            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {services.map((s, i) => (
                <div
                  key={i}
                  data-testid={`service-card-${i}`}
                  className="bg-[#0A0E17] p-8 md:p-10 hover:bg-[#131B2A] transition-colors group"
                >
                  <Car className="w-6 h-6 text-[#D95D39] mb-6" />
                  <h3 className="font-serif text-2xl text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS / TOUR PACKAGES PREVIEW */}
      <section className="bg-[#0A0E17] pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Tour packages</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-white leading-tight tracking-tight max-w-2xl">
                Where would you like to go today?
              </h2>
            </div>
            <Link
              to="/packages"
              data-testid="view-all-packages-link"
              className="text-sm uppercase tracking-widest text-[#D4AF37] hover:text-white flex items-center gap-2 transition-colors"
            >
              View all packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {destinations.map((d, i) => (
              <div
                key={i}
                data-testid={`destination-card-${i}`}
                className="group relative overflow-hidden bg-[#131B2A] aspect-[3/4]"
              >
                <img
                  src={d.img}
                  alt={d.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-[#0A0E17]/40 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="text-[10px] uppercase tracking-widest bg-[#0A0E17]/70 backdrop-blur-sm text-[#D4AF37] px-3 py-1.5 border border-[#D4AF37]/40">
                    {d.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-2 leading-tight">
                    {d.name}
                  </h3>
                  <p className="text-sm text-gray-300 leading-relaxed">{d.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative bg-[#131B2A] py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 grain opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6">
              <img src={FLEET_IMG} alt="Amyee fleet" className="w-full h-[480px] object-cover" />
            </div>
            <div className="md:col-span-6 md:pl-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Why Amyee</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-white leading-tight">
                Reliable rides,
                <span className="italic text-[#D95D39]"> at a fair price.</span>
              </h2>

              <ul className="mt-10 space-y-7">
                {[
                  { icon: Shield, t: "Safe, verified drivers", d: "Local drivers who know every backroad of Keonjhar." },
                  { icon: Clock, t: "24×7 availability", d: "Late-night pickups, early-morning temple runs — we're up." },
                  { icon: Star, t: "Transparent pricing", d: "Clear quotes upfront. No meter tricks, no surprises." },
                  { icon: MapPin, t: "Door to door", d: "Pickup from your home, drop at your doorstep." },
                ].map((f, i) => (
                  <li key={i} className="flex gap-5">
                    <div className="shrink-0 w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center">
                      <f.icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h4 className="font-serif text-xl text-white">{f.t}</h4>
                      <p className="text-sm text-gray-400 mt-1">{f.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="booking" className="bg-[#0A0E17] py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center md:text-left mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Book a ride</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white leading-tight tracking-tight">
              Tell us where you&apos;re headed.
            </h2>
            <p className="mt-5 text-gray-400 max-w-xl">
              Drop your details and we&apos;ll call back within minutes to confirm your ride.
            </p>
          </div>

          <div className="bg-[#131B2A] border border-white/5 p-8 md:p-14">
            <BookingForm />
          </div>
        </div>
      </section>

      {/* FAQ SECTION (SEO) */}
      <section className="bg-[#131B2A] border-t border-white/10 py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Frequently asked</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-white leading-tight tracking-tight mb-14">
            Taxi in Keonjhar — <span className="italic text-[#D95D39]">your questions answered.</span>
          </h2>

          <div className="space-y-8 divide-y divide-white/10">
            {[
              {
                q: "How do I book a taxi in Harichandanpur or Ghatagaon?",
                a: "Just call or WhatsApp us on 6372045675, or fill the booking form on this page. We confirm your ride within minutes and send a driver to your doorstep in Harichandanpur, Ghatagaon, Keonjhar or nearby areas.",
              },
              {
                q: "What is the starting price for a taxi in Keonjhar?",
                a: "Our taxi packages start at ₹2,799/day for a Sedan. Prices vary by vehicle type (Sedan, SUV, Innova, Tempo Traveller), distance and trip duration. Call us for an exact quote — no hidden charges.",
              },
              {
                q: "Do you provide taxi to Ghatagaon Tarini Temple for darshan?",
                a: "Yes. We are the local specialist for Ghatagaon Maa Tarini Temple pilgrimage. Half-day round-trip from Harichandanpur or Keonjhar with comfortable temple-waiting time included.",
              },
              {
                q: "Can I hire a car for Khandadhar Waterfalls or Sanaghagara?",
                a: "Absolutely. We run full-day tours to Khandadhar Falls (~140 km), Sanaghagara Waterfall, Murga Mahadev and Gonasika — the full Keonjhar sightseeing circuit — in AC Sedan or SUV.",
              },
              {
                q: "Do you offer outstation taxi from Keonjhar to Bhubaneswar / Puri / Jamshedpur?",
                a: "Yes. Door-to-door outstation service to Bhubaneswar airport, Cuttack, Puri, Konark, Chilika, Jamshedpur and Rourkela. Available as one-way or round trip in Sedan, SUV or Innova Crysta.",
              },
              {
                q: "Are you available 24×7?",
                a: "Yes, we operate round the clock. Late-night airport transfers, early-morning temple runs, hospital emergencies — call 6372045675 anytime.",
              },
              {
                q: "Which vehicles do you have?",
                a: "Sedan (Dzire, Etios), SUV (Ertiga, Innova), Premium (Innova Crysta) and Tempo Traveller for larger groups. All AC and driven by experienced local drivers.",
              },
            ].map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group pt-8 first:pt-0"
              >
                <summary className="flex items-start justify-between cursor-pointer list-none gap-6">
                  <h3 className="font-serif text-xl md:text-2xl text-white group-open:text-[#D4AF37] transition-colors">
                    {item.q}
                  </h3>
                  <span className="text-[#D4AF37] text-2xl leading-none mt-1 shrink-0 group-open:rotate-45 transition-transform duration-300">
                    +
                  </span>
                </summary>
                <p className="text-gray-400 leading-relaxed mt-4 max-w-3xl">
                  {item.a}
                </p>
              </details>
            ))}
          </div>

          {/* FAQ JSON-LD for Google rich results */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: [
                  { "@type": "Question", name: "How do I book a taxi in Harichandanpur or Ghatagaon?", acceptedAnswer: { "@type": "Answer", text: "Call or WhatsApp Amyee Travels on 6372045675 or fill the booking form on amyeetravels.in. We confirm your ride within minutes across Harichandanpur, Ghatagaon and Keonjhar." } },
                  { "@type": "Question", name: "What is the starting price for a taxi in Keonjhar?", acceptedAnswer: { "@type": "Answer", text: "Taxi packages start at ₹2,799 per day for a Sedan. Prices vary by vehicle type and distance." } },
                  { "@type": "Question", name: "Do you provide taxi to Ghatagaon Tarini Temple?", acceptedAnswer: { "@type": "Answer", text: "Yes, we run daily half-day round trips to Ghatagaon Maa Tarini Temple from Harichandanpur and Keonjhar." } },
                  { "@type": "Question", name: "Can I hire a car for Khandadhar Waterfalls or Sanaghagara?", acceptedAnswer: { "@type": "Answer", text: "Yes, we offer full-day sightseeing packages to Khandadhar Falls, Sanaghagara, Murga Mahadev and Gonasika." } },
                  { "@type": "Question", name: "Do you offer outstation taxi from Keonjhar to Bhubaneswar / Puri / Jamshedpur?", acceptedAnswer: { "@type": "Answer", text: "Yes. One-way and round-trip outstation taxi to Bhubaneswar, Cuttack, Puri, Konark, Jamshedpur and Rourkela." } },
                  { "@type": "Question", name: "Are you available 24×7?", acceptedAnswer: { "@type": "Answer", text: "Yes, Amyee Travels operates 24 hours a day, 7 days a week. Call 6372045675 anytime." } },
                  { "@type": "Question", name: "Which vehicles do you have?", acceptedAnswer: { "@type": "Answer", text: "Sedan (Dzire, Etios), SUV (Ertiga, Innova), Premium (Innova Crysta) and Tempo Traveller for larger groups. All AC vehicles with experienced local drivers." } },
                ],
              }),
            }}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
